import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import parse, { domToReact, attributesToProps } from "html-react-parser";
import { useIntl, IntlProvider } from "react-intl";
import { useSelector } from "react-redux";
import { MathJax } from "better-react-mathjax";
import { getDir } from "@learningblocks/shared-components/lib/Translations";
import "./style.scss";
import {
  CLASS_MAP,
  VOID_TAGS,
  DIRECTIONAL_TAGS,
  BASE_TAGS,
  MAX_SUFFIX,
} from "./constants";

// ----- Helpers -----
function normalizeProps(tagName, attribs, locale) {
  const props = attributesToProps(attribs);
  if (DIRECTIONAL_TAGS.includes(tagName) && !props.dir) {
    props.dir = getDir(locale);
  }
  if (tagName === "img" && !props.alt) {
    props.alt = "";
  }
  return props;
}

function renderHighlight(node, ctx) {
  const { onClickHighlight, addTagClasses, locale } = ctx;
  const { contentId, ...rest } = node.attribs;
  const Tag = node.name;
  const props = normalizeProps(Tag, rest, locale);

  const childrenJsx = domToReact(node.children, {
    replace: (child) => renderNode(child, ctx),
  });

  return (
    <Tag
      {...props}
      role="button"
      tabIndex={0}
      className={`${addTagClasses ? CLASS_MAP[Tag] : ""} highlight`}
      onClick={(e) =>
        onClickHighlight?.(
          contentId,
          e,
          rest["content-title"],
          rest["content-description"],
          rest["content-image"]
        )
      }
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") &&
        onClickHighlight?.(
          contentId,
          e,
          rest["content-title"],
          rest["content-description"],
          rest["content-image"]
        )
      }
    >
      {childrenJsx}
    </Tag>
  );
}

function renderNode(node, ctx) {
  if (node.type === "text") {
    return node.data;
  }
  if (node.type !== "tag") {
    return null;
  }
  let { name: tagName, attribs = {} } = node;
  const { children = [] } = node;
  const { raw, attributesMap, addTagClasses, className, locale } = ctx;
  // Normalize contentId for attributes coming straight from data files
  // (in case of untranslated projects) becuase html-react-parser is
  // case-insensitive and converts contentId to contentid
  attribs.contentId = attribs.contentId || attribs.contentid;
  // Handle placeholder tag suffix like b_1, b_2, etc.
  const idx = tagName.lastIndexOf("_");
  if (idx > 0) {
    const base = tagName.slice(0, idx);
    attribs = attributesMap?.[raw]?.[tagName];
    tagName = base;
  }

  // <b> with highlight
  if (
    tagName === "b" &&
    (attribs.contentId ||
      (attribs["content-title"] && attribs["content-description"]))
  ) {
    return renderHighlight({ ...node, name: tagName, attribs }, ctx);
  }

  // Void tags
  if (VOID_TAGS.includes(tagName)) {
    return React.createElement(
      tagName,
      normalizeProps(tagName, attribs, locale)
    );
  }

  // Normal tag
  const props = normalizeProps(tagName, attribs, locale);
  const childrenJsx = domToReact(children, {
    replace: (child) => renderNode(child, ctx),
  });
  const tagClass = addTagClasses ? CLASS_MAP[tagName] || "" : "";

  return React.createElement(
    tagName,
    { ...props, className: `${className} ${tagClass}`.trim() },
    childrenJsx
  );
}

// ----- Main Component -----
function RichTextInternal({
  value = "",
  text = "",
  equation = false,
  addTagClasses = true,
  onClickHighlight = undefined,
  className = "",
}) {
  const intl = useIntl();
  const attributesMap = useSelector((state) => state.dataAttributes);
  const mathJaxRef = useRef(null);

  useEffect(() => {
    if (equation && mathJaxRef.current) {
      const container = mathJaxRef.current;

      const removeTabIndexFromAllElements = () => {
        const allElements = container.querySelectorAll("*");
        allElements.forEach((element) => {
          if (element.getAttribute("tabindex") !== "-1") {
            element.setAttribute("tabindex", "-1");
          }
        });
      };

      if (window.MathJax?.typesetPromise) {
        window.MathJax.typesetPromise([container]).then(() => {
          removeTabIndexFromAllElements();
        });
      }

      removeTabIndexFromAllElements();
    }
  }, [equation, text, value]);

  let raw, defaultMessage, placeholderValues, description;
  if (typeof value === "object") {
    raw = value.id;
    defaultMessage = value.defaultMessage || value.id;
    placeholderValues = value.placeholderValues || null;
    description = value.description || "";
  } else if (typeof value === "string") {
    raw = value || text;
  }

  if (!raw) return null;
  const translated = intl.formatMessage(
    { id: raw, defaultMessage, description },
    placeholderValues
  );

  // Full context for helpers
  const ctx = {
    raw,
    attributesMap,
    addTagClasses,
    onClickHighlight,
    className,
    locale: intl.locale,
  };
  const parsedContent = parse(translated, {
    replace: (node) => renderNode(node, ctx),
  });

  if (equation) {
    return (
      <span
        ref={mathJaxRef}
        className="equation-wrapper"
        inert=""
        style={{ outline: "none" }}
      >
        <MathJax inline dynamic>
          {parsedContent}
        </MathJax>
      </span>
    );
  }

  return parsedContent;
}

// Dynamically providing tag context for suffixed tags like b_1, b_2, etc.
function makeDefaultRichTextElements() {
  const map = {};
  for (const tag of BASE_TAGS) {
    map[tag] = (chunks) => `<${tag}>${chunks.join("")}</${tag}>`;
    for (let i = 1; i <= MAX_SUFFIX; i++) {
      const tagWithIndex = `${tag}_${i}`;
      map[tagWithIndex] = (chunks) =>
        `<${tagWithIndex}>${chunks.join("")}</${tagWithIndex}>`;
    }
  }

  return map;
}
const defaultRichText = makeDefaultRichTextElements();

export function RichText(props) {
  const intl = useIntl();
  return (
    <IntlProvider
      locale={intl.locale}
      messages={intl.messages}
      defaultRichTextElements={defaultRichText}
    >
      <RichTextInternal {...props} />
    </IntlProvider>
  );
}

RichText.propTypes = {
  value:
    PropTypes.string |
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      defaultMessage: PropTypes.string,
      description: PropTypes.string,
      placeholderValues: PropTypes.object,
    }),
  text: PropTypes.string,
  equation: PropTypes.bool,
  addTagClasses: PropTypes.bool,
  onClickHighlight: PropTypes.func,
  className: PropTypes.string,
};
